/*
License

Copyright (c) 2022 Chukwubuikem Ume-Ugwa

Permission is hereby witheld, any person obtaining a copy
of this software and associated documentation files (the "Software"), cannot
use, copy, modify, merge, publish, distribute, sublicense, and/or sell
copies of the Software. These restrictions can be only lifted with
a written permission from Chukwubuikem Ume-Ugwa.
*/
import {
  FormErrorMessage,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  FormControl,
  FormLabel,
  Input,
  Button,
  ModalFooter,
  useToast,
  Select,
  Center,
  Stack,
} from '@chakra-ui/react';
import { useCallback, useRef, useState } from 'react';

import Schemas from '../api/schemas';
import { ArtPreview } from './create.queue.component';
import { createAssociatedTokenAccount, MINT_ADDRESS } from '../api/qdot';
import { PublicKey } from '@solana/web3.js';
import { useConnection, useWallet } from '@solana/wallet-adapter-react';
import { useStorage } from '../api/cache';
import Constants from '../constants';
import { firebaseFacade } from '../api/firebase';

export default function TrackUpdateModal({ track, isOpen, onClose, onUpdateListener }) {
  const { db_node_schema, track_schema } = Schemas;
  const trackTitleRef = useRef();
  const trackArtistRef = useRef();
  const trackArtRef = useRef();

  const [price, setPrice] = useState(track[track_schema.price]);
  const trackPricingUnitRef = useRef();
  const toast = useToast();

  const [titleError, setTitleError] = useState();
  const [artistError, setArtistError] = useState();
  const [show, setShow] = useState(false);

  const [opening, setOpening] = useState(false);
  const [loading, setLoading] = useState(false);
  const [art, setArt] = useState();

  const [artUri, setArtUri] = useState(track[track_schema.art_uri]);
  const [priceError, setPriceError] = useState(false);
  const {
    accountBalance: accountBalanceKey,
    associatedTokenAddress: ataKey,
    accountExistsKey,
  } = Constants;

  const [accountBalance] = useStorage(accountBalanceKey, null);
  const [accountExists, cacheAccountExists] = useStorage(
    accountExistsKey,
    false
  );

  const { publicKey, signTransaction } = useWallet();
  const { connection } = useConnection();
  const [associatedTokenAddress] = useStorage(ataKey, null);

  const onUpdate = async event => {
    if (show) {
      toast({
        title: 'No QDOT Account Found',
        description:
          'Please use the `Open` button to open QDOT account or make all track free!',
        duration: 5000,
        isClosable: true,
        status: 'info',
        position: 'top',
      });
      return;
    }

    setLoading(true);
    event.preventDefault();
    const {
      current: { value: title },
    } = trackTitleRef;

    const {
      current: { value: artist },
    } = trackArtistRef;
    const {
      current: { value: unit },
    } = trackPricingUnitRef;

    if (title === '') {
      setLoading(false);
      setTitleError('Please enter a title for your track');
      return;
    }

    if (artist === '') {
      setLoading(false);
      setArtistError('Please enter artist(s) for your track');
      return;
    }

    const trackCopy = {};
    const trackPath = `${db_node_schema.queues}/${
      track[track_schema.queue_address]
    }/${db_node_schema.tracks}/${track[track_schema.track_address]}`;
    trackCopy[track_schema.title] = title;

    trackCopy[track_schema.artist] = artist;
    trackCopy[track_schema.price] = Number.parseFloat(price);
    trackCopy[track_schema.pricing_unit] = Number.parseInt(unit);

    try {
      track[track_schema.art_uri] = trackCopy[track_schema.art_uri] =
        (art && (await firebaseFacade.uploadArt(track[track_schema.track_address], art))) ||
        track.art_uri;
      await firebaseFacade.updateDocument(trackPath, trackCopy);
      toast({
        title: track.title,
        description: 'Hooray! Your track was successfully updated!',
        duration: 5000,
        isClosable: true,
        status: 'success',
        position: 'top',
      });
      onUpdateListener({...track, ...trackCopy})
    } catch (error) {
      toast({
        title: track.title,
        description:
          'OhOh! We were unable to update your track. Please try again!',
        duration: 5000,
        isClosable: true,
        status: 'error',
        position: 'top',
      });
    }
    setPriceError(null);
    setArtistError(null);
    setTitleError(null);

    setLoading(false);
    onClose();
  };

  const handleRemove = () => {
    URL.revokeObjectURL(artUri);
    setArt(null);
    setArtUri(null);

    trackArtRef.current.value = null;
    track[
      track_schema.art_uri
    ] = `${process.env.PUBLIC_URL}/logo-512x512-bg.png`;
  };

  const handleArtSelect = ({
    target: {
      files: [art],
    },
  }) => {
    art && handleRemove();
    setArtUri(URL.createObjectURL(art));
    setArt(art);
  };

  const handleArtSelectClick = () => {
    trackArtRef.current.click();
  };

  const handleCreateQdotAccount = useCallback(async () => {
    setOpening(true);
    const verdict = associatedTokenAddress
      ? await createAssociatedTokenAccount(
          connection,
          new PublicKey(associatedTokenAddress),
          publicKey,
          MINT_ADDRESS,
          publicKey,
          signTransaction
        )
      : false;

    if (verdict) {
      const description = `Your QDOT account has been created! Visit DexLab/Raydium to purchase some QDOTS! `;
      toast({
        title: 'Account Creation Succeeded',
        description: description,
        duration: 5000,
        isClosable: true,
        status: 'success',
        position: 'top',
      });
      setShow(false);
    } else {
      const description = `Unable to create an account. Please try again or use your preferred wallet app to add the Mint`;
      toast({
        title: 'Account Creation Failed',
        description: description,
        duration: 5000,
        isClosable: true,
        status: 'error',
        position: 'top',
      });
    }

    cacheAccountExists(accountExistsKey, verdict);
    setOpening(false);
  }, [associatedTokenAddress, publicKey]);

  const handlePriceChange = ({ target: { value: price } }) => {
    price = Number.parseFloat(price === '' ? 0 : price);
    setShow(price > 0 && accountBalance === null);
    if (price < 0 || (price > 0 && price < 1e-8)) {
      setPriceError('Enter a price that is either 0 or >= 1e-8');
    } else {
      setPriceError(null);
      setPrice(price);
    }
  };

  return (
    <Modal initialFocusRef={trackTitleRef} isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Update Track</ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          <Stack spacing={5}>
            <FormControl isInvalid={titleError}>
              <FormLabel>Title</FormLabel>
              <FormErrorMessage>{titleError}</FormErrorMessage>
              <Input
                ref={trackTitleRef}
                placeholder="Enter track title"
                defaultValue={track[track_schema.title]}
              />
            </FormControl>
            <FormControl isInvalid={artistError}>
              <FormLabel>Artist(s)</FormLabel>
              <FormErrorMessage>{artistError}</FormErrorMessage>
              <Input
                ref={trackArtistRef}
                placeholder="Enter artist name(s)"
                defaultValue={track[track_schema.artist]}
              />
            </FormControl>
            {show && (
              <Center>
                <Button
                  onClick={handleCreateQdotAccount}
                  isLoading={opening}
                  isDisabled={opening}
                  mb="20px"
                >
                  Create QDOT Account
                </Button>
              </Center>
            )}
            <FormControl isInvalid={priceError}>
              <FormLabel>Price</FormLabel>
              <Input
                type={'number'}
                onChange={handlePriceChange}
                placeholder="Enter price (>= e-8)"
                defaultValue={Number.parseFloat(track[track_schema.price])}
              />
              <FormErrorMessage>{priceError}</FormErrorMessage>
            </FormControl>
            <FormControl>
              <FormLabel>Unit</FormLabel>
              <Select
                ref={trackPricingUnitRef}
                placeholder="Select pricing unit"
                defaultValue={Number.parseInt(track[track_schema.pricing_unit])}
              >
                <option value={0}>LOOPS</option>
                <option value={1}>SECONDS</option>
              </Select>
            </FormControl>
            <FormControl>
              <Center>
                <Input
                  ref={trackArtRef}
                  type={'file'}
                  accept="image/*"
                  display={'none'}
                  onChange={handleArtSelect}
                />
                <Button onClick={handleArtSelectClick}>Upload track art</Button>
              </Center>
            </FormControl>
            {artUri && <ArtPreview url={artUri} onDelete={handleRemove} />}
          </Stack>
        </ModalBody>
        <ModalFooter>
          <Button
            isLoading={loading}
            isDisabled={loading}
            onClick={onUpdate}
            bg={'transparent'}
            mr={3}
          >
            Update
          </Button>
          <Button bg={'transparent'} onClick={onClose}>
            Cancel
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
